import { ReactElement, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { TextField } from '../../../components/TextField';
import { Button } from '../../../components/Button/Button';
import { Alert } from '../../../components/Alert/Alert';
import { GhostLoader } from '../../../components/GhostLoader/GhostLoader';
import { FocusPageLayout } from '../../../components/FocusPageLayout';
import { Paragraph } from '../../../components/Paragraph/Paragraph';
import classes from './EditPartnerPage.module.css';
import { Routes } from '../../../enums/Routes';
import { useGetPartnerDetailQuery } from '../../../hooks/queries/useGetPartnerDetailQuery';
import { useUpdatePartnerMutation } from '../../../hooks/mutations/useUpdatePartnerMutation';
import {
  editPartnerSchema,
  type EditPartnerFormData,
  convertBasisPointsToPercentage,
  buildUpdatePayload,
  extractValidationErrors,
  isValidCashbackInput,
} from './EditPartnerPage.utils';

export const EditPartnerPage = (): ReactElement => {
  const { t } = useTranslation();
  const { partnerId } = useParams<{ partnerId: string }>();
  const navigate = useNavigate();
  const { data: partner, isLoading, error } = useGetPartnerDetailQuery(Number(partnerId));
  const updatePartnerMutation = useUpdatePartnerMutation();

  const [formData, setFormData] = useState<EditPartnerFormData>({
    contactEmail: '',
    contactPhone: '',
    cashbackPercentage: '',
  });

  const [errors, setErrors] = useState<Partial<Record<keyof EditPartnerFormData, string>>>({});

  useEffect(() => {
    if (partner) {
      setFormData({
        contactEmail: partner.contactEmail ?? '',
        contactPhone: partner.contactPhone ?? '',
        cashbackPercentage: convertBasisPointsToPercentage(partner.cashbackPercentage),
      });
    }
  }, [partner]);

  const updateField = (field: keyof EditPartnerFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (field === 'cashbackPercentage' && !isValidCashbackInput(value)) return;

    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (updatePartnerMutation.isPending) return;

    const validationResult = editPartnerSchema.safeParse(formData);

    if (!validationResult.success) {
      setErrors(extractValidationErrors(validationResult.error));
      return;
    }

    const payload = buildUpdatePayload(validationResult.data);

    updatePartnerMutation.mutate(
      {
        partnerId: Number(partnerId),
        payload,
      },
      {
        onSuccess: () => {
          toast.success(t('systemAdmin.editPartner.toast.updateSuccess'));
          navigate(`/system-admin/partners/${partnerId}`);
        },
        onError: () => {
          toast.error(t('systemAdmin.editPartner.toast.updateError'));
        },
      }
    );
  };

  if (isLoading) {
    return (
      <FocusPageLayout
        title={t('systemAdmin.editPartner.pageTitle')}
        backLink={Routes.SystemAdminPartners}
        backLinkLabel={t('systemAdmin.editPartner.backToPartners')}
      >
        <GhostLoader />
      </FocusPageLayout>
    );
  }

  if (error || !partner) {
    return (
      <FocusPageLayout
        title={t('systemAdmin.editPartner.pageTitle')}
        backLink={Routes.SystemAdminPartners}
        backLinkLabel={t('systemAdmin.editPartner.backToPartners')}
      >
        <Alert variant="error" title={t('systemAdmin.editPartner.errors.loadError.title')}>
          {error ? t('systemAdmin.editPartner.errors.loadError.couldNotLoad') : t('systemAdmin.editPartner.errors.loadError.notFound')}
        </Alert>
      </FocusPageLayout>
    );
  }

  return (
    <FocusPageLayout
      title={t('systemAdmin.editPartner.pageTitleWithName', { name: partner.name })}
      backLink={`/system-admin/partners/${partnerId}`}
      backLinkLabel={t('systemAdmin.editPartner.backToPartner')}
    >
      <form className={classes.form} onSubmit={handleSubmit}>
        <div className={classes.section}>
          <Paragraph size="md" className={classes.sectionTitle}>
            {t('systemAdmin.editPartner.sections.contactInfo')}
          </Paragraph>

          <TextField
            name="contactEmail"
            label={t('systemAdmin.editPartner.fields.contactEmail.label')}
            value={formData.contactEmail}
            onChange={updateField('contactEmail')}
            placeholder={t('systemAdmin.editPartner.fields.contactEmail.placeholder')}
            type="email"
            errorMessage={errors.contactEmail}
          />

          <TextField
            name="contactPhone"
            label={t('systemAdmin.editPartner.fields.contactPhone.label')}
            value={formData.contactPhone}
            onChange={updateField('contactPhone')}
            placeholder={t('systemAdmin.editPartner.fields.contactPhone.placeholder')}
            type="text"
            errorMessage={errors.contactPhone}
          />
        </div>

        <div className={classes.section}>
          <Paragraph size="md" className={classes.sectionTitle}>
            {t('systemAdmin.editPartner.sections.cashback')}
          </Paragraph>
          <Paragraph size="sm" className={classes.sectionDescription}>
            {t('systemAdmin.editPartner.cashbackDescription')}
          </Paragraph>

          <TextField
            name="cashbackPercentage"
            label={t('systemAdmin.editPartner.fields.cashbackPercentage.label')}
            value={formData.cashbackPercentage}
            onChange={updateField('cashbackPercentage')}
            placeholder={t('systemAdmin.editPartner.fields.cashbackPercentage.placeholder')}
            type="text"
            errorMessage={errors.cashbackPercentage}
          />
        </div>

        <div className={classes.actions}>
          <Button
            variant="secondary"
            size="medium"
            onClick={() => navigate(`/system-admin/partners/${partnerId}`)}
            disabled={updatePartnerMutation.isPending}
            type="button"
          >
            {t('systemAdmin.editPartner.actions.cancel')}
          </Button>
          <Button
            variant="primary"
            size="medium"
            type="submit"
            disabled={updatePartnerMutation.isPending}
          >
            {updatePartnerMutation.isPending ? t('systemAdmin.editPartner.actions.saving') : t('systemAdmin.editPartner.actions.save')}
          </Button>
        </div>
      </form>
    </FocusPageLayout>
  );
};
